import { VALID_INVESTOR_TYPES } from "../constants";
import { FormType } from "../types";

const ALLOWED_PREFILL_PARAMS = [
  "firstname",
  "lastname",
  "investortype",
  "entityname",
  "entitycountrycode",
  "entitystatecode",
  "countrycode",
  "statecode",
] as const;

type AllowedFormTypeKeys = keyof Omit<FormType, "phone">;
export type PrefillParams = Record<AllowedFormTypeKeys, string | undefined>;

const PARAM_TO_PREFILL_KEY: Record<
  (typeof ALLOWED_PREFILL_PARAMS)[number],
  AllowedFormTypeKeys
> = {
  firstname: "firstName",
  lastname: "lastName",
  countrycode: "country",
  statecode: "state",
  entityname: "entityName",
  entitycountrycode: "entityCountry",
  entitystatecode: "entityState",
  investortype: "investorType",
};

const EMPTY_PREFILL: PrefillParams = {
  firstName: "",
  lastName: "",
  country: "",
  state: "",
  entityName: "",
  entityCountry: "",
  entityState: "",
  investorType: undefined,
};

/**
 * Case-insensitive parameter lookup from URLSearchParams.
 * Returns the first value for a key that matches (case-insensitive) or null.
 */
function getParamCaseInsensitive(
  searchParams: URLSearchParams,
  paramKey: string,
): string | null {
  const keyLower = paramKey.toLowerCase();
  const entries = Array.from(searchParams.entries());
  for (const entry of entries) {
    const [key, value] = entry;
    if (key.toLowerCase() === keyLower) {
      return value;
    }
  }
  return null;
}

/**
 * Sanitize and validate a prefill value.
 * - Trim whitespace
 * - Country/state codes normalized to uppercase
 */
function sanitizeAndValidate(
  rawValue: string,
  paramKey: string,
): string | undefined {
  const trimmed = rawValue.trim();
  if (!trimmed) return "";

  switch (paramKey) {
    case "countrycode":
    case "entitycountrycode":
    case "entitystatecode":
    case "statecode":
      return trimmed.toUpperCase();

    case "investortype": {
      const lower = trimmed.toLowerCase();
      return VALID_INVESTOR_TYPES.includes(
        lower as (typeof VALID_INVESTOR_TYPES)[number],
      )
        ? lower
        : undefined;
    }

    case "firstname":
    case "lastname":
    case "entityname":
      return trimmed;

    default:
      return "";
  }
}

export function getPrefillFromSearchParams(
  searchParams: URLSearchParams,
): PrefillParams {
  const result = { ...EMPTY_PREFILL };

  for (const allowed of ALLOWED_PREFILL_PARAMS) {
    const raw = getParamCaseInsensitive(searchParams, allowed);
    if (raw === null) continue;

    const sanitized = sanitizeAndValidate(raw, allowed);
    result[PARAM_TO_PREFILL_KEY[allowed]] = sanitized;
  }

  return result;
}
